import React, { useState } from "react";
import Box from "../../Box";
import Text from "../../Text";
import Card from "../../Card";
import { Store } from "../../../types/store";
import { TerritoryRegion } from "../../../types/territoryRegion";
import { isStoreMode, isAlertMode, isStoreOrAlertMode } from "../../../utils/modeUtils";

import styled from "styled-components";
import {
  CFunctionalNotice,
  CIconButton,
  CInputText,
  CListItem,
  CTag
} from "@carrefour/design-system-components-react";
import { ModeSliceState } from "../../../slices/modeSlice";
import { useTranslation } from "react-i18next";
import { showIcon } from "../../../utils/iconUtils";

const CInputTextCustom = styled(CInputText)`
  label {
    font-size: 14px;
  }
  input::placeholder {
    font-size: 13px;
  }
`;

const CListItemCustom = styled(CListItem)`
  width: 100%;
`;
const Icon = styled(CIconButton)`
  size: 20px;
`;
const TextCustom = styled.span<{ fontSize: number, color?: string }>`
  color: ${(props) => props.color || "#575757"};
  font-size: ${(props) => props.fontSize}px;
  font-weight: bold;
  font-family: "system-ui";
`;
const Tag = styled(CTag)`
  font-family: system-ui;
`;
const ContainerList = styled(Box)`
  padding-inline: 0px;
  @media only screen and (max-width: 600px) {
    padding-inline: 0;
  }
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CardCustom = styled(Card)`
  z-index: 99999;
 position: relative;
  margin-top: 0;

  @media only screen and (max-width: 600px) {
    width: 90%;
  }

  @media (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    max-height: 90dvh;
    padding: 12px 16px !important;
  }
`;

const ContentContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  max-height: 550px;
  width: 100%;

  @media (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    max-height: calc(90dvh - 70px);
  }
`;

const SearchWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 10px;
  width: 100%;

  @media (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    margin-top: 8px;
    margin-bottom: 8px;
  }
`;

export type StoreSelectorModalProps = {
  title?: string;
  onCancel: () => void;
  storeArray: Store[] | TerritoryRegion[];
  onSelect: (storeEan: string) => void;
  selectedStore: Store | TerritoryRegion | null;
  mode: ModeSliceState["value"];
};

const StoreSelectorModal = ({
  title,
  onCancel,
  storeArray,
  onSelect,
  selectedStore,
  mode,
}: StoreSelectorModalProps) => {
  const [inputSeacrh, setInputSearch] = useState("");

  let arrayFilter: Store[] | TerritoryRegion[] = [];

  if (Array.isArray(storeArray)) {
    if (isStoreOrAlertMode(mode)) {
      arrayFilter = (storeArray as Store[]).filter((e) =>
        e.storeName?.toLowerCase()?.includes(inputSeacrh.toLowerCase())
          || e.storeKey?.toLowerCase()?.includes(inputSeacrh.toLowerCase())
            || (e.leaseManagementTypeName && e.leaseManagementTypeName.toLowerCase().includes(inputSeacrh.toLowerCase()))
      );
    } else {
      arrayFilter = (storeArray as TerritoryRegion[]).filter((e) => (
          e.name?.toLowerCase()?.includes(inputSeacrh.toLowerCase())
          || e.key?.toLowerCase()?.includes(inputSeacrh.toLowerCase())
        )
      );
    }
  }

  const handleSelect = (storeEan: string) => {
    onSelect(storeEan);
  };

  const {t} = useTranslation();

  return (
    <CardCustom minWidth={["90%", "518px"]} className="modal">
      <Box display="flex" justifyContent={"center"} position={"relative"}>
        <Box display="flex" flexDirection="row">
          <Text variant="title5" color={"black"}>
            {title ?? `${t('common.choisir_un')} ${isStoreOrAlertMode(mode) ? t('common.magasin') : t('common.territoire_ou_region')}`}
          </Text>
        </Box>
        <Box
          display={"flex"}
          alignItems={"center"}
          position={"absolute"}
          right={0}
          top={["-30%", "-30%"]}
        >
          <Icon
            data-testid="close-button"
            onClick={onCancel}
            icon={"symbol-times"}
            variation={"tertiary"}
            tone={"monochrome"}
            size={"m"}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        justifySelf={"center"}
        alignItems={"center"}
        width={"100"}
        px={[0, 6]}
      >
        <ContentContainer>
          <SearchWrapper>
            <CInputTextCustom
              width={"100%"}
              placeholder={`${t('common.chercher_un')} ${
                isStoreOrAlertMode(mode) ? t('common.magasin') : t('common.territoire_ou_region')
              }*`}
              appendIcon="search"
              name="example"
              type="text"
              label={`${t('common.nom_du')} ${isStoreOrAlertMode(mode) ? t('common.magasin') : t('common.territoire_ou_dela_region')}`}
              required
              showClearTextButton={false}
              value={inputSeacrh}
              onKeyUp={(event) =>
                setInputSearch(String(event.currentTarget.value))
              }
            />
          </SearchWrapper>
          <ContainerList overflow="auto" width={"100%"}>
            {arrayFilter.length === 0 ? 
            <CFunctionalNotice
              closable
              closeButtonAriaLabel="Close"
              priority="high"
              show
              status="error"
              title={isStoreOrAlertMode(mode) ? t('common.magasin_inexistant') : t('common.territoire_ou_region_inexistant')}
            >
              {t('common.territoire_ou_region_inexistant_description')}
            </CFunctionalNotice>            
            : arrayFilter.map((store: Store | TerritoryRegion) => {
              const selected =
                isStoreOrAlertMode(mode)
                  ? (selectedStore as Store)?.storeKey ===
                    (store as Store)?.storeKey
                  : (selectedStore as TerritoryRegion)?.key ===
                    (store as TerritoryRegion)?.key;
              return (
                <Box
                  my={1}
                  key={`select-store-${
                    isStoreOrAlertMode(mode)
                      ? (store as Store).storeKey
                      : (store as TerritoryRegion).key
                  }`}
                  borderBottom="1px solid"
                  borderColor="grey2"
                  width={"100%"}
                >
                  <CListItemCustom
                    size="s"
                    onClick={() =>
                      handleSelect(
                        isStoreOrAlertMode(mode)
                          ? (store as Store).storeKey
                          : (store as TerritoryRegion).key
                      )
                    }
                    tone="default"
                    appendContent={
                      selected && (
                        <Tag
                          icon="check-circle-full"
                          status="success"
                          size="s"
                          hasBorder={false}
                        >
                          {t('common.selectionne')}
                        </Tag>
                      )
                    }
                  >
                    <Box display="flex" alignItems="center">
                    <IconContainer>
                      { showIcon(store as TerritoryRegion, mode) }
                    </IconContainer>
                      <Box ml={2}>
                        <TextCustom fontSize={14} color="#575757">
                          {isStoreMode(mode) || isAlertMode(mode)
                            ? (store as Store)?.storeName
                            : (store as TerritoryRegion)?.name}
                        </TextCustom>
                        {(isStoreMode(mode) || isAlertMode(mode)) && (store as Store)?.leaseManagementTypeName && (
                          <TextCustom fontSize={12} color="#a9a9a9">
                            {` - ${(store as Store)?.leaseManagementTypeName}`}
                          </TextCustom>
                        )}
                      </Box>
                    </Box>
                  </CListItemCustom>
                </Box>
              );
            })}
          </ContainerList>
        </ContentContainer>
      </Box>
    </CardCustom>
  );
};

export default StoreSelectorModal;
